import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#f8f9fa] via-white to-[#f1f3f5]">
      {/* Top Contact Bar */}
      <div className="hidden md:block bg-[#1a365d] text-white text-xs py-2 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-end gap-6">
          <span className="flex items-center gap-1.5">
            <i className="ri-phone-line"></i>
            (02) 2736-1182
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-map-pin-line"></i>
            台北市信義區基隆路二段189號3樓之5
          </span>
          <a href="mailto:complus@ms21.hinet.net" className="flex items-center gap-1.5 hover:text-gray-300 transition-colors">
            <i className="ri-mail-line"></i>
            complus@ms21.hinet.net
          </a>
        </div>
      </div>

      {/* Navigation */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        {/* Top Contact Bar inside fixed header */}
        <div className="hidden md:block bg-[#1a365d] text-white text-xs py-2 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-end gap-6">
            <span className="flex items-center gap-1.5">
              <i className="ri-phone-line"></i>
              (02) 2736-1182
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line"></i>
              台北市信義區基隆路二段189號3樓之5
            </span>
            <a href="mailto:complus@ms21.hinet.net" className="flex items-center gap-1.5 hover:text-gray-300 transition-colors">
              <i className="ri-mail-line"></i>
              complus@ms21.hinet.net
            </a>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/6a7615f77dc3602f828c8823dd35e1ca/c0b6d62b076790e2d2f1a96025f6ffbe.png"
                  alt="康普斯智產權事務所 Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <h1 className="text-xl font-bold text-[#1a365d]" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                康普斯智產權事務所
              </h1>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-sm font-medium text-gray-700 hover:text-[#2c5282] transition-colors cursor-pointer whitespace-nowrap"
              >
                首頁
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="text-sm font-medium text-gray-700 hover:text-[#2c5282] transition-colors cursor-pointer whitespace-nowrap"
              >
                服務項目
              </button>
              <button
                onClick={() => navigate('/about-ip')}
                className="text-sm font-medium text-gray-700 hover:text-[#2c5282] transition-colors cursor-pointer whitespace-nowrap"
              >
                關於我們
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-6 py-2.5 bg-[#2c5282] text-white rounded-lg font-medium hover:bg-[#1a365d] transition-all cursor-pointer whitespace-nowrap"
              >
                聯絡我們
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-6 py-4 space-y-3">
              <div className="pb-3 border-b border-gray-100 space-y-1.5">
                <p className="text-xs text-gray-500 flex items-center gap-1.5"><i className="ri-phone-line"></i>(02) 2736-1182</p>
                <p className="text-xs text-gray-500 flex items-center gap-1.5"><i className="ri-mail-line"></i>complus@ms21.hinet.net</p>
              </div>
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setMenuOpen(false);
                }}
                className="block w-full text-left text-gray-700 hover:text-[#2c5282] font-medium cursor-pointer"
              >
                首頁
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="block w-full text-left text-gray-700 hover:text-[#2c5282] font-medium cursor-pointer"
              >
                服務項目
              </button>
              <button
                onClick={() => {
                  navigate('/about-ip');
                  setMenuOpen(false);
                }}
                className="block w-full text-left text-gray-700 hover:text-[#2c5282] font-medium cursor-pointer"
              >
                關於我們
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left bg-[#2c5282] text-white px-4 py-2 rounded-lg hover:bg-[#1a365d] cursor-pointer"
              >
                聯絡我們
              </button>
            </div>
          </motion.div>
        )}
      </motion.header>

      {/* Hero Content */}
      <div className="relative pt-40 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="inline-block px-4 py-2 bg-[#2c5282]/10 rounded-full mb-6"
              >
                <span className="text-sm font-semibold text-[#2c5282]">專業智慧財產權顧問</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a365d] mb-6 leading-tight"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                守護您的創新成果
                <br />
                <span className="text-[#2c5282]">讓智慧財產權成為競爭優勢</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-lg text-gray-600 mb-8 leading-relaxed"
              >
                專為新創公司、中小企業與個人創作者提供專業的智慧財產權諮詢服務。從專利、商標到著作權，我們用清楚易懂的方式，協助您建立完整的智產權保護策略。
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(44,82,130,0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection('contact')}
                  className="px-8 py-4 bg-[#2c5282] text-white rounded-lg font-semibold hover:bg-[#1a365d] transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>預約初步諮詢</span>
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/dc5f4c9738cdf9dc9285399fd85f0aa2.jpg"
                  alt="智慧財產權諮詢"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d]/20 to-transparent"></div>
              </div>

              {/* Floating Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-gray-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#2c5282]/10 rounded-lg">
                    <i className="ri-shield-check-line text-2xl text-[#2c5282]"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#1a365d]">專業可靠</div>
                    <div className="text-sm text-gray-600">值得信賴的智產權夥伴</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2"
          >
            <div className="w-6 h-10 border-2 border-[#2c5282]/30 rounded-full flex items-start justify-center p-2">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-1.5 bg-[#2c5282] rounded-full"
              />
            </div>
            <span className="text-xs text-gray-500 uppercase tracking-wider">向下探索</span>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}